import { useCallback, useEffect, useMemo, useState } from "react";
import { TfiCheck, TfiReload, TfiTrash } from "react-icons/tfi";
import Select from "react-select";
import type { ColDef } from "ag-grid-community";
import type { CustomCellEditorProps } from "ag-grid-react";
import { AgGridReact } from "ag-grid-react";
import _ from "lodash";
import { useRecoilState, useRecoilValue } from "recoil";
import { v4 as uuid } from "uuid";

import type { IconProviderStatus } from "@components/table/ActionCellRenderer.tsx";
import ActionCellRenderer, {
  getActionCellClickHandler,
} from "@components/table/ActionCellRenderer.tsx";

import {
  COUNTRY_CODES,
  CURRENCY_CODES,
  getCountryName,
  getCurrencyName,
} from "@lib/countries.ts";
import {
  getHighlightChangesCellFormatter,
  getHighlightChangesCellStyler,
  getRowUpdateHandler,
} from "@lib/grid.ts";
import type { EditablePricing } from "@pages/ProductCatalog/store";
import {
  editProductPricingsState,
  productDetails,
  resetEdits,
  selectedProductState,
} from "@pages/ProductCatalog/store";

import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";

const createNewPricing = (): EditablePricing => ({
  id: "",
  originalId: "",
  amount: 0,
  strikeThroughAmount: 0,
  currency: "",
  countryCode: "",
  promotionSlug: null,
  active: true,
});

export default function PricingsList() {
  const reset = useRecoilValue(resetEdits);
  const productId = useRecoilValue(selectedProductState);
  const product = useRecoilValue(productDetails(productId));
  const [rows, setRows] = useRecoilState(editProductPricingsState);

  const byPricingId = useMemo(() => {
    // Grid rows key on a string id; server ids are numbers, and update is
    // replace-all so they don't need to round-trip.
    const withId: EditablePricing[] = (product?.pricings || []).map((data) => ({
      id: String(data.id),
      originalId: String(data.id),
      amount: parseFloat(`${data.amount}`) || 0,
      strikeThroughAmount: parseFloat(`${data.strikeThroughAmount}`) || 0,
      currency: data.currency,
      countryCode: data.countryCode,
      startDate: data.startDate,
      endDate: data.endDate,
      promotionSlug: data.promotionSlug,
      active: data.active,
    }));
    return _.keyBy(withId, "id");
  }, [product, reset]);

  const lookupById: (id: string) => EditablePricing = useCallback(
    (id: string) => byPricingId[id],
    [byPricingId],
  );

  useEffect(() => {
    setRows([...Object.values(byPricingId), createNewPricing()]);
  }, [byPricingId]);

  const highlightChangesCellStyler = useCallback(
    getHighlightChangesCellStyler<EditablePricing>(lookupById),
    [lookupById],
  );

  const highlightChangesCellFormatter = useCallback(
    getHighlightChangesCellFormatter(lookupById, "-"),
    [lookupById],
  );

  const actionHandler = useMemo(
    () => getActionCellClickHandler(lookupById, createNewPricing, setRows),
    [lookupById],
  );

  const columnDefs: ColDef<EditablePricing>[] = useMemo(
    () => [
      {
        headerName: "",
        field: "id",
        editable: false,
        cellRenderer: ActionCellRenderer,
        cellRendererParams: {
          icons: (data: EditablePricing, { isEditing }: IconProviderStatus) =>
            _.merge(
              data.id && { delete: TfiTrash },
              data.originalId &&
                !_.isEqual(data, lookupById(data.originalId)) && {
                  revert: TfiReload,
                },
              isEditing && { done: TfiCheck },
            ),
          onClick: actionHandler,
        },
        cellStyle: highlightChangesCellStyler,
      },
      {
        headerName: "Country",
        field: "countryCode",
        filter: "agTextColumnFilter",
        autoHeight: true,
        wrapText: true,
        cellEditorPopup: true,
        valueFormatter: (param) => {
          const code = highlightChangesCellFormatter(param) || "";
          const countryName = getCountryName(code);
          return countryName ? `${countryName} (${code})` : code;
        },
        editable: true,
        cellEditor: ({
          eGridCell,
          value,
          onValueChange,
        }: CustomCellEditorProps<EditablePricing, string>) => {
          const defaultValue = value
            ? { value, label: `${getCountryName(value)} (${value})` }
            : null;
          const [selectedValue, setSelectedValue] = useState(defaultValue);

          useEffect(() => {
            onValueChange(selectedValue?.value ?? "");
          }, [selectedValue]);

          return (
            <Select
              value={selectedValue}
              styles={{
                container: () => ({
                  width: eGridCell.style.width,
                }),
              }}
              options={COUNTRY_CODES.map((value) => ({
                value,
                label: `${getCountryName(value)} (${value})`,
              }))}
              onChange={(v) => setSelectedValue(v)}
            />
          );
        },
        cellEditorParams: {
          values: COUNTRY_CODES,
        },
        cellStyle: highlightChangesCellStyler,
      },
      {
        headerName: "Amount",
        field: "amount",
        filter: "agNumberColumnFilter",
        cellDataType: "number",
        editable: true,
        cellEditor: "agNumberCellEditor",
        cellStyle: highlightChangesCellStyler,
        valueFormatter: highlightChangesCellFormatter,
      },
      {
        headerName: "Strike Through",
        field: "strikeThroughAmount",
        filter: "agNumberColumnFilter",
        cellDataType: "number",
        editable: true,
        cellEditor: "agNumberCellEditor",
        cellStyle: highlightChangesCellStyler,
        valueFormatter: highlightChangesCellFormatter,
      },
      {
        headerName: "Currency",
        field: "currency",
        filter: "agTextColumnFilter",
        valueFormatter: (param) => {
          const maybeValue = highlightChangesCellFormatter(param) || "";
          const currencyName = getCurrencyName(maybeValue);
          return currencyName ? `${currencyName} (${maybeValue})` : maybeValue;
        },
        editable: true,
        autoHeight: true,
        wrapText: true,
        cellEditor: ({
          eGridCell,
          value,
          onValueChange,
        }: CustomCellEditorProps<EditablePricing, string>) => {
          value = value || "USD";
          const defaultValue = {
            value,
            label: `${getCurrencyName(value)} (${value})`,
          };
          const [selectedValue, setSelectedValue] = useState(defaultValue);

          useEffect(() => {
            onValueChange(selectedValue.value);
          }, [selectedValue]);

          return (
            <Select
              value={selectedValue}
              styles={{
                container: () => ({
                  width: eGridCell.style.width,
                }),
              }}
              options={CURRENCY_CODES.map((value) => ({
                value,
                label: `${getCurrencyName(value)} (${value})`,
              }))}
              onChange={(v) => setSelectedValue({ ...v! })}
            />
          );
        },
        cellEditorParams: {
          values: CURRENCY_CODES,
        },
        cellEditorPopup: true,
        cellStyle: highlightChangesCellStyler,
      },
      {
        headerName: "Valid From",
        field: "startDate",
        filter: "agDateColumnFilter",
        editable: true,
        cellEditor: "agDateCellEditor",
        cellStyle: highlightChangesCellStyler,
        valueFormatter: highlightChangesCellFormatter,
      },
      {
        headerName: "Valid Until",
        field: "endDate",
        filter: "agDateColumnFilter",
        editable: true,
        cellEditor: "agDateCellEditor",
        cellStyle: highlightChangesCellStyler,
        valueFormatter: highlightChangesCellFormatter,
      },
      {
        headerName: "Active",
        field: "active",
        cellDataType: "boolean",
        editable: true,
        cellRenderer: "agCheckboxCellRenderer",
        cellEditor: "agCheckboxCellEditor",
        cellStyle: highlightChangesCellStyler,
      },
    ],
    [
      lookupById,
      actionHandler,
      highlightChangesCellStyler,
      highlightChangesCellFormatter,
    ],
  );

  const onRowEdit = useMemo(
    () =>
      getRowUpdateHandler<EditablePricing>(
        lookupById,
        ["countryCode", "currency", "startDate", "endDate"],
        createNewPricing,
        setRows,
        ({ data }) => data.id,
        uuid,
        [
          ({ originalId, countryCode }: EditablePricing) =>
            !!originalId && !countryCode && "Pricing must have a country",
        ],
      ),
    [],
  );

  return (
    <div className="ag-theme-quartz w-full h-full">
      {productId !== undefined ? (
        <AgGridReact<EditablePricing>
          reactiveCustomComponents={true}
          getRowId={({ data }) => data.id}
          autoSizeStrategy={{ type: "fitCellContents" }}
          editType="fullRow"
          onRowEditingStarted={({ api, node }) => {
            api.refreshCells({
              rowNodes: [node],
              columns: ["id", "currency", "countryCode"],
              force: true,
            });
          }}
          popupParent={document.querySelector("body")}
          // @ts-ignore
          columnDefs={columnDefs}
          rowData={rows.map((row) => ({ ...row }))}
          onRowValueChanged={onRowEdit}
        />
      ) : (
        "Select a product in the product list"
      )}
    </div>
  );
}
